import React, {useEffect, useRef, useState} from 'react';

import Information from './information/Information.jsx';
import Reviews from './reviews/Reviews.jsx';
import ReviewsAPI from '../apis/reviews-api';
import Sustainability from './sustainability/Sustainability.jsx';
import ProductMessages from './message-list';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import Landmark from '@core/scripts/components/landmark-adapter';
import ProductionFacilities from './production-facilities/ProductionFacilities.jsx';
import {createSustainabilityContext} from "@product/data/sustainability/sustainability-context.ts";
import {Icon, List, Spinner} from '@bonprix/design-system';
import Breakpoint from "@core/scripts/helper/breakpoint";
import {SideSectionAction} from "@product/sections/SideSectionAction.tsx";
import {Accordion, Rating, Typography} from "bonprix-design-system";
import Messages from "@core/scripts/components/messages.js";

let toggleReviewAccordionItemContent;

export default function ProductAccordion({
                                             texts,
                                             product,
                                             isLoggedIn,
                                             loginLink,
                                             glossaryData,
                                             reviewContent,
                                             staticLinks,
                                             contentContainerRef
                                         }) {
    const [reviewModel, setReviewModel] = useState(null);
    const [currentSelectedStyle, setCurrentSelectedStyle] = useState(product.currentStyle);
    const [currentSelectedItem, setCurrentSelectedItem] = useState(product.currentItem);
    const [currentSelectedItemOption, setCurrentSelectedItemOption] = useState(product.currentItemOption);
    const [showReviewContent, setShowReviewContent] = useState(false);
    const [reviewsError, setReviewsError] = useState(false);
    const [mobileView, setMobileView] = useState(Breakpoint.keyIsIn(['start', 'small', 'medium']));
    const [loading, setLoading] = useState(false);

    const productAccordionRef = useRef(null);
    const callbackCalled = useRef({});
    const firstInViewLandmarksSent = useRef([]);

    useEffect(() => {
        toggleReviewAccordionItemContent?.();
    }, [showReviewContent]);

    useEffect(() => {
        if (!mobileView) {
            // handle visible landmarks for list items, which don't have a dedicated onInitialInView callback
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        const type = entry.target.querySelector('[data-item-type]')?.dataset.itemType;
                        if (type && !firstInViewLandmarksSent.current.includes(type)) {
                            onAccordionItemInView(type);
                            firstInViewLandmarksSent.current.push(type);
                        }
                    }
                }
            );

            if (productAccordionRef.current) {
                productAccordionRef.current.querySelectorAll('li')
                    .forEach((item) => observer.observe(item));
            }

            return () => {
                if (productAccordionRef.current) {
                    observer.unobserve(productAccordionRef.current);
                }
            };
        }
    }, []);

    useEffect(() => {
        const subscriptions = [
            MessageBroker.subscribe(ProductMessages.CHANGED, onProductChanged),
            MessageBroker.subscribe(ProductMessages.SCROLL_TO_REVIEWS, scrollToProductAccordion),
            MessageBroker.subscribe(ProductMessages.OPEN_SIDE_SHEET_FOR_REVIEWS, onReviewAccordionItemOpen),
            MessageBroker.subscribe(Messages.BREAKPOINT.CHANGED, isMobileView)
        ];

        return () => {
            subscriptions.forEach(MessageBroker.unsubscribe);
        };
    }, []);

    const isMobileView = () => setMobileView(Breakpoint.keyIsIn(['start', 'small', 'medium']));

    const onProductChanged = ({style, item, itemOption}) => {
        if (style) {
            setCurrentSelectedStyle(style);
        }
        if (item) {
            setCurrentSelectedItem(item);
        }
        if (itemOption) {
            setCurrentSelectedItemOption(itemOption);
        }
    };

    const onReviewAccordionItemOpen = () => {
        setReviewsError(false);
        if (!showReviewContent) {
            setLoading(true);
            if (!reviewModel) {
                ReviewsAPI.loadReviews(product.shopId, product.styleId).then((reviewModel) => {
                    if (reviewModel?.reviewContent) {
                        setReviewModel(reviewModel);
                        Landmark.sendClickLandmark(`adp_reviews_show\\${currentSelectedItem.itemNumber}`);
                        setShowReviewContent(true);
                        setLoading(false);
                        if (!mobileView) {
                            MessageBroker.publish(SideSectionAction.OPEN, {
                                nodes: {
                                    title: texts.ada15_reviews_headline,
                                    content: <Reviews
                                        reviewModel={reviewModel}
                                        isLoggedIn={isLoggedIn}
                                        loginLink={loginLink}
                                        currentSelectedItem={currentSelectedItem}
                                    />
                                }
                            });
                        }
                    } else {
                        setReviewsError(true);
                        setLoading(false);
                        if (!mobileView) {
                            MessageBroker.publish(SideSectionAction.OPEN, {
                                nodes: {
                                    title: texts.ada15_reviews_headline,
                                    content: <div className="reviews__error-message">{texts.bff_reviews_error}</div>
                                }
                            });
                        }
                    }
                })
            }
        } else {
            Landmark.sendClickLandmark(`adp_reviews_show\\${currentSelectedItem.itemNumber}`);
            if (!mobileView) {
                MessageBroker.publish(SideSectionAction.OPEN, {
                    nodes: {
                        title: texts.ada15_reviews_headline,
                        content: <Reviews
                            reviewModel={reviewModel}
                            isLoggedIn={isLoggedIn}
                            loginLink={loginLink}
                            currentSelectedItem={currentSelectedItem}
                        />
                    }
                });
            }
        }
    };

    const scrollToProductAccordion = () => {
        if (!Breakpoint.keyIsIn(['start', 'small', 'medium'])) {
            const offset = 300;
            const elementPosition = contentContainerRef.current?.getBoundingClientRect().bottom || 0;
            window.scrollTo({
                top: elementPosition - offset,
                behavior: 'smooth'
            });
        } else {
            productAccordionRef.current?.scrollIntoView({behavior: 'smooth'});
        }
    };

    const onSustainabilityAccordionItemOpen = () => {
        if (!mobileView) {
            MessageBroker.publish(SideSectionAction.OPEN, {
                nodes: {
                    title: sustainabilityContext.contentElements.accordionHeadline,
                    content: <Sustainability
                        sustainabilityContext={sustainabilityContext}
                        glossaryData={glossaryData}
                    />
                }
            });
        }
        Landmark.sendClickLandmark(`adp_sustainability_show\\${currentSelectedItem.itemNumber}`);
    };
    const onProductionFacilitiesAccordionItemOpen = () => {
        if (!mobileView) {
            MessageBroker.publish(SideSectionAction.OPEN, {
                nodes: {
                    title: texts.production_facilities_title,
                    content:
                        <ProductionFacilities
                            productionFacilities={productionFacilities}
                            texts={texts}
                            moreLinkUrl={staticLinks.productionFacilitiesMoreLinkTarget}
                        />
                }
            });
        }
        Landmark.sendClickLandmark(`adp_factories_show\\${currentSelectedItem.itemNumber}`);
    };

    const onProductInformationAccordionItemOpened = () => {
        if (!mobileView) {
            MessageBroker.publish(SideSectionAction.OPEN, {
                nodes: {
                    title: texts.ada19_productinformation,
                    content: <Information product={product}
                                          staticLinks={staticLinks}
                                          texts={texts}
                                          style={currentSelectedStyle}
                                          item={currentSelectedItem}
                                          itemOption={currentSelectedItemOption}
                    />
                }
            });
        }
        Landmark.sendClickLandmark(`adp_productinfo_show\\${currentSelectedItem.itemNumber}`);
    };

    const onAccordionItemInView = (type) => {
        Landmark.sendVisibleLandmark(`adp\\${type}\\${currentSelectedItem.itemNumber}`);
    };

    const currentReviewContent = reviewModel?.reviewContent || reviewContent;
    const productionFacilities = currentSelectedItem?.productionFacilities;

    const sustainabilityContext = createSustainabilityContext(
        currentSelectedItem,
        staticLinks,
    );


    function useCallbackOnce(callback) {
        return () => {
            if (!callbackCalled.current[callback]) {
                callbackCalled.current[callback] = true;
                callback?.();
            }
        };
    }

    if (mobileView) {
        return (
            <div ref={productAccordionRef}
                 className="product__accordion__full_width">
                <Accordion>
                    <Accordion.Item type="default"
                                    title={texts.ada19_productinformation}
                                    onOpen={onProductInformationAccordionItemOpened}
                                    onInitialInView={useCallbackOnce(() => onAccordionItemInView('productinfo'))}>
                        <Information product={product}
                                     staticLinks={staticLinks}
                                     texts={texts}
                                     style={currentSelectedStyle}
                                     item={currentSelectedItem}
                                     itemOption={currentSelectedItemOption}/>
                    </Accordion.Item>
                    {sustainabilityContext && (
                        <Accordion.Item type="sustainable"
                                        features={sustainabilityContext.features}
                                        title={sustainabilityContext.contentElements.accordionHeadline}
                                        onInitialInView={useCallbackOnce(() => onAccordionItemInView('sustainability'))}
                                        onOpen={onSustainabilityAccordionItemOpen}>
                            <Sustainability
                                sustainabilityContext={sustainabilityContext}
                                glossaryData={glossaryData}
                            />
                        </Accordion.Item>
                    )}
                    {productionFacilities?.length > 0 && (
                        <Accordion.Item type="default"
                                        title={texts.production_facilities_title}
                                        onInitialInView={useCallbackOnce(() => onAccordionItemInView('factories'))}
                                        onOpen={onProductionFacilitiesAccordionItemOpen}>
                            <ProductionFacilities
                                productionFacilities={productionFacilities}
                                texts={texts}
                                moreLinkUrl={staticLinks.productionFacilitiesMoreLinkTarget}
                            />
                        </Accordion.Item>
                    )}
                    {currentReviewContent && (
                        <Accordion.Item type="rating"
                                        onInitialInView={useCallbackOnce(() => onAccordionItemInView('reviews'))}
                                        onOpen={onReviewAccordionItemOpen}
                                        title={texts.ada15_reviews_headline}
                                        rating={reviewContent.averageRating}
                                        ratingQuantity={reviewContent?.count}>
                            {loading ? (
                                <div className="reviews__spinner-container"><Spinner edgeLength="32px"/></div>
                            ) : (
                                showReviewContent && (
                                    <Reviews
                                        reviewModel={reviewModel}
                                        isLoggedIn={isLoggedIn}
                                        loginLink={loginLink}
                                        currentSelectedItem={currentSelectedItem}
                                    />
                                )
                            )}
                            {reviewsError && <div className="reviews__error-message">{texts.bff_reviews_error}</div>}
                        </Accordion.Item>
                    )}
                </Accordion>
            </div>
        )
    }

    return (
        <div ref={productAccordionRef} className="product__accordion__full_width">
            <List type="button" showArrows>
                <List.Item onClick={onProductInformationAccordionItemOpened}>
                    <span data-item-type="productinfo">{texts.ada19_productinformation}</span>
                </List.Item>

                {sustainabilityContext && (
                    <List.Item onClick={onSustainabilityAccordionItemOpen}>
                        <div className="flex gap-2 items-center">
                            <span data-item-type="sustainability">{sustainabilityContext.contentElements.accordionHeadline}</span>
                                {sustainabilityContext.features.map((feature) => (
                                <Icon key={feature} variant={'sustainable-' + feature}/>
                            ))}
                        </div>
                    </List.Item>
                )}

                {productionFacilities?.length > 0 && (
                    <List.Item onClick={onProductionFacilitiesAccordionItemOpen}>
                        <span data-item-type="factories">{texts.production_facilities_title}</span>
                    </List.Item>
                    )}

                {currentReviewContent && (
                    <List.Item onClick={onReviewAccordionItemOpen}>
                        <div className="flex gap-2 items-center">
                            <span data-item-type="reviews">{texts.ada15_reviews_headline}</span>
                                <span className="flex gap-1.25 items-center">
                                <Rating
                                    rating={reviewContent.averageRating}
                                    size="small"
                                />
                                <Typography variant="body1">{'(' + reviewContent?.count + ')'}</Typography>
                            </span>
                            {loading && <Spinner edgeLength="24px"/>}
                        </div>
                    </List.Item>
                )}
            </List>
        </div>
    )
}
